import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, UserPlus, Plus, Trash2, Copy, Check, KeyRound } from 'lucide-react';
import { superAdminApi } from '../../api/superAdmin.js';
import { accountsApi } from '../../api/accounts.js';
import { copyText } from '../../lib/clipboard.js';
import { overlayDismiss } from '../../lib/overlayDismiss';

const HOST_RE = /^[a-z][a-z0-9-]*$/;
const DEFAULT_DEALER_EMAIL = 'qubby_ivr@osensetech.com';
const REGIONS = ['tw', 'th'];

/**
 * CreateAccountModal — 超管中心「建立帳號」
 *   經銷商：email + 顯示名稱（席次不限）
 *   商用帳號：+ 所屬經銷商（預設 osense）+ AA/CS 席次
 *   兩者皆可同時開新 tenant（hostId 命名 + 顯示名稱 + 區域）
 * 後端 POST /api/ivr/saas-admin/parents 一次到位（帳號+tenant+席次+membership）。
 */
export function CreateAccountModal({ dealers = [], onClose, onCreated, asDealer = false }) {
    const { t } = useTranslation('superAdmin');
    const defaultDealer = dealers.find((d) => d.email === DEFAULT_DEALER_EMAIL) || dealers[0] || null;

    // 經銷商模式：固定開 business、不選經銷商（createdBy 後端強制 = 自己）
    const [role, setRole] = useState('business');
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [password, setPassword] = useState('');
    const [dealerUid, setDealerUid] = useState(defaultDealer?.uid || '');
    // 客服類型：開 AA / CS（可複選）。角色數＝計費單位（CS 每角色建置費、AA 每角色月費），
    // 席次＝共用話機池（可註冊分機數，CS 坐席月費按席次收）
    const [aaOn, setAaOn] = useState(false);
    const [csOn, setCsOn] = useState(true);
    const [roleAA, setRoleAA] = useState(1);
    const [roleCS, setRoleCS] = useState(1);
    const [seatAA, setSeatAA] = useState(1);
    const [seatCS, setSeatCS] = useState(1);
    const [tenants, setTenants] = useState([{ hostId: '', displayName: '', region: 'tw' }]);
    // 租戶來源：new=開新租戶（預設）；shared=共用既有租戶（商用子帳號：同一台 PBX / 同一份分機設定，
    // 帳務與角色分開算，租戶設定唯讀）。只有商用帳號能選 shared。
    const [tenantMode, setTenantMode] = useState('new');
    const [sharedHostId, setSharedHostId] = useState('');
    const [sharedOptions, setSharedOptions] = useState(null);   // null=未載入
    useEffect(() => {
        if (tenantMode !== 'shared' || sharedOptions !== null) return;
        accountsApi.dealerTenants()
            .then((r) => setSharedOptions(r?.tenants || []))
            .catch(() => setSharedOptions([]));
    }, [tenantMode, sharedOptions]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);   // { parent, oneTimePassword? }
    const [copied, setCopied] = useState(false);

    const setTenant = (i, patch) => setTenants((cur) => cur.map((tn, j) => (j === i ? { ...tn, ...patch } : tn)));
    const filledTenants = tenants.filter((tn) => tn.hostId.trim());
    const badHost = filledTenants.find((tn) => !HOST_RE.test(tn.hostId.trim()));
    // 母帳號一定要至少綁一個 tenant，否則登入第三關（tenants/{hostId}/members）過不了 → no_tenant_access。
    // dealer / business 皆強制（避免建出登不進後台的孤兒帳號）。
    const isShared = role === 'business' && tenantMode === 'shared';
    const canSubmit = /\S+@\S+\.\S+/.test(email.trim()) && !busy
        && (isShared ? !!sharedHostId : (!badHost && filledTenants.length > 0))
        && (role !== 'business' || ((asDealer || dealerUid) && (aaOn || csOn)));

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        try {
            const payload = {
                email: email.trim(),
                accountRole: role,
                ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
                ...(password.trim() ? { password: password.trim() } : {}),
                ...(role === 'business' ? {
                    ...(asDealer ? {} : { createdBy: dealerUid }),
                    roleQuota: {
                        plan_01: aaOn ? (Number(roleAA) || 0) : 0,
                        plan_02: csOn ? (Number(roleCS) || 0) : 0,
                    },
                    seats: {
                        plan_01: aaOn ? (Number(seatAA) || 0) : 0,
                        plan_02: csOn ? (Number(seatCS) || 0) : 0,
                    },
                } : {}),
                ...(isShared ? { sharedHostId } : (filledTenants.length ? {
                    tenants: filledTenants.map((tn) => ({
                        hostId: tn.hostId.trim(),
                        displayName: tn.displayName.trim() || tn.hostId.trim(),
                        region: tn.region,
                    })),
                } : {})),
            };
            const data = await superAdminApi.createParent(payload);
            setResult(data);
            onCreated?.();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    const copyPw = async () => {
        const ok = await copyText(result?.oneTimePassword || '');
        setCopied(ok);
        if (ok) setTimeout(() => setCopied(false), 1500);
    };

    const inputCls = 'w-full px-3 py-2 rounded-md border border-cream-300 bg-white text-sm text-ink-900 placeholder:text-ink-300 focus:outline-2 focus:outline-accent-500/60';
    const labelCls = 'block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 mb-1.5';

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 p-4 overflow-y-auto" {...overlayDismiss(onClose)}>
            <div
                className="w-full max-w-lg my-8 rounded-xl bg-cream-50 border border-cream-200 shadow-xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-labelledby="create-account-title"
            >
                <div className="px-6 pt-5 pb-3 flex items-start gap-3">
                    <span className="grid place-items-center size-9 rounded-full bg-accent-50 shrink-0">
                        <UserPlus size={18} className="text-accent-700" />
                    </span>
                    <div className="min-w-0">
                        <h2 id="create-account-title" className="heading-display text-lg font-semibold text-ink-900">
                            {result ? t('create.successTitle') : t('create.title')}
                        </h2>
                        {!result && <p className="mt-0.5 text-xs text-ink-400">{t('create.subtitle')}</p>}
                    </div>
                    <button onClick={onClose} className="ml-auto p-1 rounded text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition focus-visible:outline-2 focus-visible:outline-accent-500">
                        <X size={16} />
                    </button>
                </div>

                {result ? (
                    /* ── 成功畫面：帳號摘要 + 一次性密碼 ── */
                    <div className="px-6 pb-5 space-y-3">
                        <div className="rounded-lg border border-cream-200 bg-white/50 px-4 py-3 text-sm space-y-1">
                            <div><span className="text-ink-400">Email：</span><span className="font-mono text-ink-900">{result.parent?.email}</span></div>
                            <div><span className="text-ink-400">{t('create.typeLabel')}：</span>{t(`role.${result.parent?.accountRole}`)}</div>
                            {result.parent?.seats && (
                                <div><span className="text-ink-400">{t('create.seats')}：</span><span className="tnum">AA {result.parent.seats.plan_01 ?? 0} · CS {result.parent.seats.plan_02 ?? 0}</span></div>
                            )}
                            {(result.parent?.tenants || []).length > 0 && (
                                <div><span className="text-ink-400">Tenants：</span><span className="font-mono">{result.parent.tenants.join(', ')}</span>
                                    {result.parent?.sharedHostId && <span className="ml-2 text-[11px] text-accent-700">{t('create.sharedResult', { hostId: result.parent.sharedHostId })}</span>}
                                </div>
                            )}
                        </div>
                        {result.oneTimePassword && (
                            <div className="rounded-lg border border-accent-300 bg-accent-50/60 px-4 py-3">
                                <div className="flex items-center gap-1.5 text-xs font-medium text-accent-700 mb-1.5">
                                    <KeyRound size={13} /> {t('create.oneTimePassword')}
                                </div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 px-3 py-2 rounded bg-white border border-cream-200 font-mono text-sm text-ink-900 select-all">
                                        {result.oneTimePassword}
                                    </code>
                                    <button onClick={copyPw} className="inline-flex items-center gap-1 px-2.5 py-2 rounded-md border border-cream-300 text-xs text-ink-700 hover:bg-cream-100 transition focus-visible:outline-2 focus-visible:outline-accent-500">
                                        {copied ? <Check size={13} className="text-success-500" /> : <Copy size={13} />}
                                        {copied ? t('create.copied') : t('create.copy')}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end">
                            <button onClick={onClose} className="px-4 py-1.5 rounded-md bg-ink-900 text-cream-50 text-sm font-medium hover:opacity-90 transition focus-visible:outline-2 focus-visible:outline-accent-500 focus-visible:outline-offset-2">
                                {t('create.done')}
                            </button>
                        </div>
                    </div>
                ) : (
                    /* ── 表單 ── */
                    <div className="px-6 pb-5 space-y-4">
                        {/* 帳號類型（經銷商模式固定 business、不顯示切換）*/}
                        {!asDealer && (
                        <div>
                            <label className={labelCls}>{t('create.typeLabel')}</label>
                            <div className="grid grid-cols-2 gap-2">
                                {['dealer', 'business'].map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        onClick={() => setRole(r)}
                                        className={`px-3 py-2 rounded-md border text-sm transition focus-visible:outline-2 focus-visible:outline-accent-500 ${
                                            role === r
                                                ? 'border-accent-500 bg-accent-50 text-accent-700 font-medium'
                                                : 'border-cream-300 text-ink-500 hover:bg-cream-100'}`}
                                    >
                                        {t(`create.type${r === 'dealer' ? 'Dealer' : 'Business'}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <label className={labelCls}>Email *</label>
                                <input autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('create.displayName')}</label>
                                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('create.password')}</label>
                                <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('create.passwordAuto')} className={inputCls} />
                            </div>
                        </div>

                        {role === 'business' && (
                            <>
                                {!asDealer && (
                                <div>
                                    <label className={labelCls}>{t('create.dealer')}</label>
                                    <select value={dealerUid} onChange={(e) => setDealerUid(e.target.value)} className={inputCls}>
                                        {dealers.map((d) => (
                                            <option key={d.uid} value={d.uid}>
                                                {d.name || d.email}{d.email === DEFAULT_DEALER_EMAIL ? ` — ${t('create.dealerDefault')}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                )}
                                <div>
                                    <label className={labelCls}>{t('create.planLabel')}</label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {[
                                            { key: 'aa', on: aaOn, setOn: setAaOn, roleQty: roleAA, setRoleQty: setRoleAA, qty: seatAA, setQty: setSeatAA, name: t('create.planAA'), desc: t('create.planAADesc') },
                                            { key: 'cs', on: csOn, setOn: setCsOn, roleQty: roleCS, setRoleQty: setRoleCS, qty: seatCS, setQty: setSeatCS, name: t('create.planCS'), desc: t('create.planCSDesc') },
                                        ].map((p) => (
                                            <div
                                                key={p.key}
                                                onClick={() => p.setOn(!p.on)}
                                                className={`rounded-md border px-3 py-2.5 cursor-pointer transition ${
                                                    p.on ? 'border-accent-500 bg-accent-50/60' : 'border-cream-300 hover:bg-cream-100'}`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className={`size-3.5 rounded-sm border grid place-items-center ${p.on ? 'bg-accent-500 border-accent-500' : 'border-cream-300 bg-white'}`}>
                                                        {p.on && <Check size={10} className="text-white" />}
                                                    </span>
                                                    <span className={`text-sm font-medium ${p.on ? 'text-accent-700' : 'text-ink-700'}`}>{p.name}</span>
                                                </div>
                                                <p className="mt-1 ml-5.5 text-[11px] text-ink-400 leading-snug">{p.desc}</p>
                                                {p.on && (
                                                    <div className="mt-2 ml-5.5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[11px] text-ink-500 w-12">{t('create.roleQty')}</span>
                                                            <input
                                                                type="number" min="1" value={p.roleQty}
                                                                onChange={(e) => p.setRoleQty(e.target.value)}
                                                                className="w-20 px-2 py-1.5 rounded-md border border-cream-300 bg-white text-sm tnum text-ink-900 focus:outline-2 focus:outline-accent-500/60"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[11px] text-ink-500 w-12">{t('create.qty')}</span>
                                                            <input
                                                                type="number" min="1" value={p.qty}
                                                                onChange={(e) => p.setQty(e.target.value)}
                                                                className="w-20 px-2 py-1.5 rounded-md border border-cream-300 bg-white text-sm tnum text-ink-900 focus:outline-2 focus:outline-accent-500/60"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <p className="mt-1.5 text-[11px] text-ink-400">{t('create.seatsHint')}</p>
                                </div>
                            </>
                        )}

                        {/* 商用帳號：租戶來源切換（開新租戶 / 共用既有租戶＝商用子帳號）*/}
                        {role === 'business' && (
                        <div>
                            <label className={labelCls}>{t('create.tenantMode')}</label>
                            <div className="grid grid-cols-2 gap-2">
                                {['new', 'shared'].map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => setTenantMode(m)}
                                        className={`px-3 py-2 rounded-md border text-sm transition focus-visible:outline-2 focus-visible:outline-accent-500 ${
                                            tenantMode === m
                                                ? 'border-accent-500 bg-accent-50 text-accent-700 font-medium'
                                                : 'border-cream-300 text-ink-500 hover:bg-cream-100'}`}
                                    >
                                        {t(m === 'new' ? 'create.tenantModeNew' : 'create.tenantModeShared')}
                                    </button>
                                ))}
                            </div>
                        </div>
                        )}

                        {isShared ? (
                        <div>
                            <label className={labelCls}>{t('create.sharedTenant')}</label>
                            {sharedOptions === null ? (
                                <div className="text-xs text-ink-400 py-2 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> …</div>
                            ) : (
                                <select value={sharedHostId} onChange={(e) => setSharedHostId(e.target.value)} className={inputCls}>
                                    <option value="">{t('create.sharedTenantPick')}</option>
                                    {/* 同經銷商的租戶排前面；超管可選任何租戶（經銷商端後端只放行自己名下的） */}
                                    {[...sharedOptions]
                                        .sort((a, b) => ((b.dealerUid === dealerUid) - (a.dealerUid === dealerUid)) || String(a.hostId).localeCompare(String(b.hostId)))
                                        .map((tn) => (
                                            <option key={tn.hostId} value={tn.hostId}>{tn.displayName || tn.hostId} — {tn.hostId}</option>
                                        ))}
                                </select>
                            )}
                            {sharedOptions !== null && sharedOptions.length === 0 && <p className="mt-1 text-xs text-danger-500">{t('create.errNoSharedTenant')}</p>}
                            <p className="mt-1 text-[11px] text-ink-400">{t('create.sharedTenantHint')}</p>
                        </div>
                        ) : (
                        /* tenants 命名：識別碼 + 名稱 + 開在哪個國家（決定服務區機房）*/
                        <div>
                            <label className={labelCls}>{t('create.tenants')}</label>
                            <div className="grid grid-cols-[1fr_1fr_7rem_1.75rem] gap-2 mb-1">
                                <span className="text-[11px] text-ink-400">{t('create.tenantHostId')}</span>
                                <span className="text-[11px] text-ink-400">{t('create.tenantName')}</span>
                                <span className="text-[11px] text-ink-400">{t('create.tenantCountry')}</span>
                                <span />
                            </div>
                            <div className="space-y-2">
                                {tenants.map((tn, i) => (
                                    <div key={i} className="grid grid-cols-[1fr_1fr_7rem_1.75rem] gap-2 items-center">
                                        <input
                                            value={tn.hostId}
                                            onChange={(e) => setTenant(i, { hostId: e.target.value.toLowerCase() })}
                                            placeholder="my-company"
                                            className={`${inputCls} font-mono ${tn.hostId && !HOST_RE.test(tn.hostId) ? 'border-danger-500/60' : ''}`}
                                        />
                                        <input
                                            value={tn.displayName}
                                            onChange={(e) => setTenant(i, { displayName: e.target.value })}
                                            placeholder={t('create.tenantNamePh')}
                                            className={inputCls}
                                        />
                                        <select value={tn.region} onChange={(e) => setTenant(i, { region: e.target.value })} className="px-2 py-2 rounded-md border border-cream-300 bg-white text-sm text-ink-900 focus:outline-2 focus:outline-accent-500/60">
                                            {REGIONS.map((r) => <option key={r} value={r}>{t(`create.region${r.toUpperCase()}`)}</option>)}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => setTenants((cur) => cur.filter((_, j) => j !== i))}
                                            disabled={tenants.length === 1}
                                            className="p-1.5 rounded text-ink-300 hover:text-danger-500 hover:bg-danger-100/50 disabled:opacity-30 transition focus-visible:outline-2 focus-visible:outline-danger-500"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => setTenants((cur) => [...cur, { hostId: '', displayName: '', region: 'tw' }])}
                                className="mt-2 inline-flex items-center gap-1 text-xs text-accent-700 hover:underline focus-visible:outline-2 focus-visible:outline-accent-500"
                            >
                                <Plus size={13} /> {t('create.addTenant')}
                            </button>
                            {badHost && <p className="mt-1 text-xs text-danger-500">{t('create.errInvalidHost')}</p>}
                            {!badHost && filledTenants.length === 0 && <p className="mt-1 text-xs text-danger-500">{t('create.errNoTenant')}</p>}
                            <p className="mt-1 text-[11px] text-ink-400">{t('create.tenantsHint')}</p>
                        </div>
                        )}

                        {error && <p className="text-xs text-danger-500">{error}</p>}

                        <div className="pt-1 flex items-center justify-end gap-2">
                            <button onClick={onClose} className="px-3.5 py-1.5 rounded-md text-sm text-ink-700 hover:bg-cream-100 transition focus-visible:outline-2 focus-visible:outline-accent-500">
                                {t('create.cancel')}
                            </button>
                            <button
                                onClick={submit}
                                disabled={!canSubmit}
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-ink-900 text-cream-50 text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition focus-visible:outline-2 focus-visible:outline-accent-500 focus-visible:outline-offset-2"
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                                {t('create.submit')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
